import type { Session } from '../types/session';

/**
 * Shape of a raw session item as returned by the API.
 * Covers all endpoints: /v2/sessions, /v1/psi-platform/psychologist/sessions,
 * and the timeline endpoint.
 */
export interface RawSessionItem {
  id: string;
  created_at?: string;
  updated_at?: string;
  date: string;
  acuity_id?: string;
  status?: string;
  notes?: string | null;
  reschedule_link?: string;
  scheduled_by?: string;
  /** Si es la primera sesión del paciente (mismo criterio que el chip "Primera vez" en Sesiones) */
  first_session?: boolean;
  process?:
    | {
        id?: string;
        type?: { id?: string; name?: string };
        status?: { id?: string; name?: string };
        price?: { id?: string };
      }
    | string;
  psychologist?:
    | { id?: string; name?: string; surname?: string; email?: string }
    | string;
  patients?: Array<{
    id?: string;
    name?: string;
    surname?: string;
    email?: string;
    timezone?: string;
  }>;
  payments?: Array<{
    id?: string;
    gateway_reference?: string;
    gateway?: string;
    status?: string;
    created_at?: string;
  }>;
}

/**
 * Maps a single raw API session item to the typed `Session` domain object.
 *
 * Handles all edge cases:
 * - `psychologist` may be a bare string ID or a full object.
 * - `process` may be a bare string ID or a full object (with optional type/status/price).
 * - All optional fields default to `undefined` rather than `null` to stay consistent
 *   with the `Session` type.
 * - Patient `timezone` is preserved when present.
 *
 * @param raw - Raw session item received from the API.
 * @returns A fully-typed `Session` object.
 */
export function mapSessionResponse(raw: RawSessionItem): Session {
  // --- patients ---
  const patients = Array.isArray(raw.patients)
    ? raw.patients
        .filter(Boolean)
        .map((p) => ({
          id: p.id ?? '',
          name: [p.name, p.surname].filter(Boolean).join(' ').trim(),
          email: p.email ?? '',
          timezone: p.timezone || undefined,
        }))
    : [];

  // --- psychologist ---
  const psychologist =
    typeof raw.psychologist === 'string'
      ? { id: raw.psychologist, name: '' }
      : raw.psychologist
      ? {
          id: raw.psychologist.id ?? '',
          name: [raw.psychologist.name, raw.psychologist.surname]
            .filter(Boolean)
            .join(' ')
            .trim(),
          email: raw.psychologist.email,
        }
      : undefined;

  // --- process ---
  const processId =
    typeof raw.process === 'string' ? raw.process : (raw.process?.id ?? '');
  const processInfo =
    typeof raw.process === 'string' ? undefined : raw.process;

  // --- payments ---
  const payments = Array.isArray(raw.payments)
    ? raw.payments.map((p) => ({
        id: p.id ?? '',
        gateway_reference: p.gateway_reference,
        gateway: p.gateway,
        status: p.status,
        created_at: p.created_at,
      }))
    : undefined;

  return {
    id: raw.id,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? raw.created_at ?? '',
    process: processId,
    date: raw.date,
    acuity_id: raw.acuity_id ?? '',
    status: raw.status ?? '',
    notes: raw.notes ?? undefined,
    reschedule_link: raw.reschedule_link ?? undefined,
    scheduled_by: raw.scheduled_by ?? undefined,
    isFirst: raw.first_session,
    patients,
    psychologist,
    process_info: processInfo
      ? {
          type_name:
            processInfo.type && 'name' in processInfo.type
              ? processInfo.type.name
              : undefined,
          status_name:
            processInfo.status && 'name' in processInfo.status
              ? processInfo.status.name
              : undefined,
          price_id: processInfo.price?.id,
        }
      : undefined,
    payments,
  } as Session;
}
